/**
 * StageDigestDrilldownDialog — lists the order forms behind a clicked count.
 *
 * When the company uses running numbers the list is grouped by Page No
 * (reference_no) exactly like the Activity Digest drill-down; otherwise it is a
 * flat grid. Every order number links to its Order Form detail page, opened in
 * a NEW browser tab.
 */
import React, { useMemo, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { FileText, User, Hammer, ExternalLink, Inbox, Download, Loader2 } from 'lucide-react';
import { api } from '@/lib/api';
import { toast } from 'sonner';

export const StageDigestDrilldownDialog = ({
  open, onOpenChange, title, subtitle, orders = [],
  columnKey = '', groupBy = 'customer', companyId = null,
  usesReferenceNumbers = false, referenceLabel = 'Page No.',
}) => {
  const [downloading, setDownloading] = useState(false);

  const groupByLabel = groupBy === 'goldsmith' ? 'Goldsmith-wise' : 'Customer-wise';

  const downloadExcel = async () => {
    if (!orders.length || downloading) return;
    setDownloading(true);
    try {
      const res = await api.post(
        '/reports/production-stage-digest/export',
        {
          order_ids: orders.map((o) => o.id).filter(Boolean),
          column: columnKey || '',
          group_by: groupBy || 'customer',
          title: title || '',
          company_id: companyId || null,
        },
        { responseType: 'blob' },
      );
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      const safe = (columnKey || 'digest').replace(/[^a-z0-9_-]+/gi, '_');
      a.download = `production-digest-${groupBy}-${safe}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Excel downloaded · ${groupByLabel}`);
    } catch (e) {
      toast.error('Excel download failed. Please try again.');
    } finally {
      setDownloading(false);
    }
  };

  // Build page-grouped structure (memoised so re-renders stay cheap).
  const groups = useMemo(() => {
    if (!usesReferenceNumbers) return null;
    const map = new Map();
    const order = [];
    for (const o of orders) {
      const pno = (o.reference_no === null || o.reference_no === undefined || o.reference_no === '') ? null : String(o.reference_no);
      const k = pno === null ? '__nopage__' : pno;
      if (!map.has(k)) { map.set(k, { page_no: pno, rows: [] }); order.push(k); }
      map.get(k).rows.push(o);
    }
    // Numeric-aware sort of pages; the "no page" bucket sinks to the bottom.
    order.sort((a, b) => {
      if (a === '__nopage__') return 1;
      if (b === '__nopage__') return -1;
      const na = Number(a), nb = Number(b);
      if (!Number.isNaN(na) && !Number.isNaN(nb)) return na - nb;
      return a.localeCompare(b);
    });
    return order.map((k) => map.get(k));
  }, [orders, usesReferenceNumbers]);

  const OrderLink = ({ o }) => (
    <a
      href={`/orders/${o.id}`}
      target="_blank"
      rel="noopener noreferrer"
      className="group inline-flex items-center gap-1 rounded-md border border-[var(--divider)] bg-white px-2.5 py-1.5 text-[12.5px] font-semibold text-[var(--diamond-icon)] transition-colors hover:border-[var(--diamond-icon)] hover:bg-[var(--diamond-bg)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--diamond-icon)]"
      data-testid="stage-digest-order-link"
      title={`Open ${o.order_no} in a new tab`}
    >
      <FileText size={12} className="opacity-70" />
      <span className="tabular-nums">{o.order_no}</span>
      <ExternalLink size={11} className="opacity-0 transition-opacity group-hover:opacity-70" />
    </a>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl w-[94vw] max-h-[82vh] overflow-y-auto" data-testid="stage-digest-drilldown-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-[var(--ink-navy)]">
            {title}
            <span className="inline-flex items-center rounded-full bg-[var(--diamond-bg)] px-2 py-0.5 text-[11px] font-semibold text-[var(--diamond-icon)] tabular-nums" data-testid="stage-digest-drilldown-count">
              {orders.length} order{orders.length === 1 ? '' : 's'}
            </span>
          </DialogTitle>
        </DialogHeader>
        {subtitle && <div className="-mt-1 mb-1 text-xs text-slate-500">{subtitle}</div>}

        {orders.length > 0 && (
          <div className="mb-2 flex items-center justify-between gap-2 rounded-lg border border-[var(--divider)] bg-[var(--paper-warm)] px-3 py-2">
            <span className="text-[11.5px] text-slate-500">
              Export grouped <span className="font-semibold text-[var(--ink-navy)]">{groupByLabel}</span> (matches your current view)
            </span>
            <button
              type="button"
              onClick={downloadExcel}
              disabled={downloading}
              className={`inline-flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-[12.5px] font-semibold transition-colors ${
                downloading
                  ? 'cursor-not-allowed border-[var(--divider)] bg-slate-100 text-slate-400'
                  : 'border-[var(--gold-accent)] bg-[var(--champagne)] text-[var(--ink-navy)] hover:bg-[var(--gold-accent)] hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--gold-accent)]'
              }`}
              data-testid="stage-digest-export-excel"
              title={`Download these ${orders.length} order forms as Excel, grouped ${groupByLabel}`}
            >
              {downloading ? <Loader2 size={13} className="animate-spin" /> : <Download size={13} />}
              {downloading ? 'Preparing…' : 'Download Excel'}
            </button>
          </div>
        )}

        {orders.length === 0 ? (
          <div className="flex flex-col items-center gap-2 rounded-xl border border-dashed border-[var(--divider)] bg-[var(--paper-warm)] px-4 py-10 text-center text-sm text-slate-500" data-testid="stage-digest-drilldown-empty">
            <Inbox size={22} className="text-slate-300" />
            No order forms in this cell.
          </div>
        ) : usesReferenceNumbers ? (
          <div className="space-y-3" data-testid="stage-digest-drilldown-grouped">
            {groups.map((g, gi) => (
              <div key={gi} className="rounded-xl border border-[var(--divider)] overflow-hidden">
                <div className="flex items-center gap-2 border-l-4 border-[var(--gold-accent)] bg-[var(--champagne)] px-3 py-1.5">
                  {g.page_no !== null ? (
                    <a
                      href={`/orders?reference_no=${encodeURIComponent(g.page_no)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 rounded text-[12px] font-bold uppercase tracking-[0.05em] text-[var(--ink-navy)] transition-colors hover:text-[var(--diamond-icon)] hover:underline underline-offset-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--diamond-icon)]"
                      data-testid="stage-digest-drilldown-page"
                      title={`Open all order forms on ${referenceLabel} ${g.page_no} in a new tab`}
                    >
                      {`${referenceLabel} ${g.page_no}`}
                      <ExternalLink size={11} className="opacity-60" />
                    </a>
                  ) : (
                    <span className="text-[12px] font-bold uppercase tracking-[0.05em] text-[var(--ink-navy)]" data-testid="stage-digest-drilldown-page">
                      {`No ${referenceLabel}`}
                    </span>
                  )}
                  <span className="text-[11px] font-medium text-[var(--goldsmith-icon)] tabular-nums">· {g.rows.length}</span>
                </div>
                <div className="flex flex-wrap gap-2 bg-white px-3 py-2.5">
                  {g.rows.map((o) => <OrderLink key={o.id} o={o} />)}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="flex flex-wrap gap-2 rounded-xl border border-[var(--divider)] bg-white px-3 py-3" data-testid="stage-digest-drilldown-flat">
            {orders.map((o) => <OrderLink key={o.id} o={o} />)}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default StageDigestDrilldownDialog;
